import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './Auth/AuthContext';
import { fetchAuthSession } from '@aws-amplify/auth';
import SEO from './SEO';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Pricing() {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const { isAuthenticated, isSubscribed, setIsSubscribed } = useAuth();
  const navigate = useNavigate();

  // Check subscription status when component mounts
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (!isAuthenticated) {
        setCheckingStatus(false);
        return;
      }

      try {
        const authSession = await fetchAuthSession();
        const idToken = authSession.tokens?.idToken?.toString();

        if (!idToken) {
          setCheckingStatus(false);
          return;
        }

        const { data } = await axios.get(`${API_BASE_URL}/api/auth/subscription-status`, {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          },
          timeout: 10000
        });

        setIsSubscribed(data.isSubscribed);

        if (data.isSubscribed) {
          setMessage('You already have an active subscription!');
          setIsError(false);
        }
      } catch (err) {
        console.error('Pricing: Failed to check subscription status:', err);
      } finally {
        setCheckingStatus(false);
      }
    };

    checkSubscriptionStatus();
  }, [isAuthenticated, setIsSubscribed]);

  const handleSubscribe = async (planId = 'basic-monthly') => {
    if (!isAuthenticated) {
      navigate('/SignIn', { state: { from: '/pricing', plan: planId } });
      return;
    }

    if (isSubscribed) {
      setMessage('You already have an active subscription!');
      setIsError(false);
      return;
    }

    setIsLoading(true);
    setMessage('');
    let idToken;

    try {
      const authSession = await fetchAuthSession();
      idToken = authSession.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('Failed to get authentication token');
      }

      const { data } = await axios.get(`${API_BASE_URL}/api/auth/subscription-status`, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000
      });

      if (data.isSubscribed) {
        setMessage('You are already subscribed!');
        setIsError(false);
        setIsLoading(false);
        return;
      }

      const res = await axios.post(`${API_BASE_URL}/api/auth/create-checkout-session`,
        { planId, origin: window.location.origin },
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          },
          timeout: 10000
        }
      );

      window.location.href = res.data.url;
    } catch (err) {
      console.error('Pricing: Checkout error:', err.response?.data || err.message);

      const errorCode = err.response?.data?.errorCode;
      const errorMessage = err.response?.data?.error || err.response?.data?.message;

      if (errorCode === 'ALREADY_SUBSCRIBED') {
        setMessage('You already have an active subscription!');
        setIsError(false);
      } else if (errorCode === 'CHECKOUT_IN_PROGRESS') {
        setMessage(errorMessage || 'A checkout is already in progress. Please wait.');
        setIsError(true);
      } else {
        setMessage(errorMessage || 'Failed to load payment page. Please try again.');
        setIsError(true);
      }
      setIsLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center font-quicksand">
        <div className="text-4xl mb-4 animate-pulse">⏳</div>
        <p className="text-study-black text-xl">Checking subscription status...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-quicksand">
      <SEO
        title="Pricing"
        description="Simple, affordable pricing for StudyPass RDH — unlimited access to NBDHE practice quizzes, study guides, flashcards, and videos."
        path="/pricing"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-study-pink to-study-teal py-16 px-6 text-center">
        <h1 className="text-4xl md:text-5xl font-bold text-study-black mb-4">
          Simple, Affordable Pricing
        </h1>
        <p className="text-xl text-study-black max-w-2xl mx-auto">
          Get unlimited access to everything you need to pass your boards
        </p>
      </section>

      {/* Message Banner */}
      {message && (
        <div className="max-w-4xl mx-auto px-4 pt-6">
          <div className={`p-4 rounded-xl text-center ${
            isError
              ? 'bg-red-100 border-2 border-red-400'
              : 'bg-green-100 border-2 border-green-400'
          }`}>
            <p className="text-study-black font-semibold">{message}</p>
          </div>
        </div>
      )}

      {/* Pricing Cards */}
      <section className="max-w-5xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">

          {/* Free Plan */}
          <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 shadow-md">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-study-black mb-2">Free</h2>
              <div className="text-4xl font-bold text-study-black mb-1">$0</div>
              <p className="text-gray-500">Forever free</p>
            </div>

            <ul className="space-y-4 mb-8">
              <FeatureItem included={true} text="1 quiz per day" />
              <FeatureItem included={true} text="Access to all subjects" />
              <FeatureItem included={true} text="Progress tracking" />
              <FeatureItem included={false} text="Unlimited quizzes" />
              <FeatureItem included={false} text="Study guides" />
              <FeatureItem included={false} text="Flashcards" />
              <FeatureItem included={false} text="Priority support" />
            </ul>

            {isAuthenticated ? (
              <button
                disabled
                className="w-full py-3 px-6 rounded-full font-bold text-study-black bg-gray-200 cursor-default"
              >
                Current Plan
              </button>
            ) : (
              <button
                onClick={() => navigate('/SignUp')}
                className="w-full py-3 px-6 rounded-full font-bold text-study-black bg-gray-200 hover:bg-gray-300 transition-all"
              >
                Get Started
              </button>
            )}
          </div>

          {/* Premium Plan */}
          <div className="bg-gradient-to-br from-study-yellow to-study-pink rounded-2xl p-8 shadow-xl relative overflow-hidden">
            {/* Popular Badge */}
            <div className="absolute top-4 right-4 bg-study-teal text-study-black text-sm font-bold px-3 py-1 rounded-full">
              Most Popular
            </div>

            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-study-black mb-2">Premium</h2>
              <div className="text-4xl font-bold text-study-black mb-1">$20</div>
              <p className="text-study-black">per month</p>
            </div>

            <ul className="space-y-4 mb-8">
              <FeatureItem included={true} text="Unlimited quizzes" highlight />
              <FeatureItem included={true} text="Access to all subjects" highlight />
              <FeatureItem included={true} text="Progress tracking" highlight />
              <FeatureItem included={true} text="All study guides" highlight />
              <FeatureItem included={true} text="All flashcard decks" highlight />
              <FeatureItem included={true} text="Detailed analytics" highlight />
              <FeatureItem included={true} text="Priority support" highlight />
            </ul>

            {isSubscribed ? (
              <div className="text-center">
                <div className="w-full py-3 px-6 rounded-full font-bold text-study-black bg-white mb-3">
                  ✓ You're Subscribed!
                </div>
                <button
                  onClick={() => navigate('/')}
                  className="text-study-black font-semibold hover:underline"
                >
                  Go to Dashboard →
                </button>
              </div>
            ) : (
              <button
                onClick={() => handleSubscribe('premium-monthly')}
                disabled={isLoading}
                className="w-full py-4 px-6 rounded-full font-bold text-lg text-study-black bg-study-teal hover:bg-white transition-all transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin">⏳</span> Processing...
                  </span>
                ) : (
                  'Start Premium'
                )}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="bg-gray-50 py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-study-black text-center mb-12">
            What's Included in Premium
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <IncludedCard
              icon="📝"
              title="Unlimited Quizzes"
              description="Take as many practice quizzes as you want, anytime. No daily limits."
            />
            <IncludedCard
              icon="📚"
              title="Study Guides"
              description="Comprehensive PDF guides covering every topic on the board exams."
            />
            <IncludedCard
              icon="🗂️"
              title="Flashcards"
              description="Over 2,000 flashcards to reinforce key concepts and terminology."
            />
            <IncludedCard
              icon="📊"
              title="Progress Analytics"
              description="Track your performance by subject and identify areas to improve."
            />
            <IncludedCard
              icon="🎯"
              title="Board-Style Questions"
              description="Questions designed to match the format and difficulty of real exams."
            />
            <IncludedCard
              icon="💬"
              title="Priority Support"
              description="Get help when you need it with faster response times."
            />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="max-w-3xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-study-black text-center mb-12">
          Frequently Asked Questions
        </h2>
        <div className="space-y-6">
          <FAQItem
            question="Can I cancel anytime?"
            answer="Yes! You can cancel your subscription at any time. You'll continue to have access until the end of your billing period."
          />
          <FAQItem
            question="Is there a free trial?"
            answer="We offer a free plan that lets you take 1 quiz per day. This lets you try out the platform before committing to Premium."
          />
          <FAQItem
            question="What payment methods do you accept?"
            answer="We accept all major credit cards (Visa, Mastercard, American Express) through our secure payment processor, Stripe."
          />
          <FAQItem
            question="Can I switch plans later?"
            answer="Absolutely! You can upgrade to Premium at any time, or cancel and return to the free plan."
          />
        </div>
      </section>

      {/* Final CTA */}
      {!isSubscribed && (
        <section className="bg-gradient-to-r from-study-teal to-study-pink py-16 px-6 text-center">
          <h2 className="text-3xl font-bold text-study-black mb-4">
            Ready to Pass Your Boards?
          </h2>
          <p className="text-study-black text-lg mb-8 max-w-xl mx-auto">
            Join thousands of students who have used StudyPass to prepare for their exams.
          </p>
          <button
            onClick={() => isAuthenticated ? handleSubscribe('premium-monthly') : navigate('/SignUp')}
            disabled={isLoading}
            className="inline-block bg-study-yellow text-study-black font-bold text-lg py-4 px-8 rounded-full hover:bg-white transition-all transform hover:scale-105 shadow-lg disabled:opacity-50"
          >
            {isAuthenticated ? 'Get Premium Now' : 'Start Free Today'}
          </button>
        </section>
      )}
    </div>
  );
}

// ============================================================================
// Reusable Components
// ============================================================================

function FeatureItem({ included, text, highlight = false }) {
  return (
    <div className="flex items-center gap-3">
      {included ? (
        <span className="text-green-500 text-xl">✓</span>
      ) : (
        <span className="text-gray-400 text-xl">✗</span>
      )}
      <span className={`${highlight ? 'text-study-black' : included ? 'text-study-black' : 'text-gray-400'}`}>
        {text}
      </span>
    </div>
  );
}

function IncludedCard({ icon, title, description }) {
  return (
    <div className="text-center">
      <div className="text-4xl mb-3">{icon}</div>
      <h3 className="text-lg font-bold text-study-black mb-2">{title}</h3>
      <p className="text-gray-600 text-sm">{description}</p>
    </div>
  );
}

function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-gray-200 pb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between text-left"
      >
        <span className="text-lg font-semibold text-study-black">{question}</span>
        <span className="text-2xl text-study-teal">{isOpen ? '−' : '+'}</span>
      </button>
      {isOpen && (
        <p className="mt-3 text-gray-600">{answer}</p>
      )}
    </div>
  );
}

export default Pricing;
